// « Extraire les données » : pour qui, périmètre, période, contenu, formats, puis le récapitulatif des fichiers.
// Téléphone : page plein écran (/parcelles/:id/extraction) ; ordinateur : panneau à droite de la fiche.
// Hors réseau, la demande est mise en file et part au retour du réseau (le fichier rejoint les Documents).
import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, CloudOff, Download, FileArchive, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { authFetch } from "@/lib/api";
import { demandesEnFile, mettreEnFile, QUEUE_EVENT } from "@/lib/extractionQueue";
import { API, COL, isErreur } from "./shared";

export const USAGES = {
  controle: { libelle: "Contrôle DAAF / SRAL", detail: "Registre et identification, XLSX + PDF, année civile",
    rubriques: ["exploitation", "localisation", "ilots", "registre"], formats: ["xlsx", "pdf"], periode: "annee", sansIdentite: false },
  technicien: { libelle: "Technicien ou coopérative", detail: "Sans SIRET ni PACAGE, PDF, campagne",
    rubriques: ["localisation", "ilots", "sol", "registre", "fertilisation", "biostimulants", "irrigation", "chantiers", "sante"],
    formats: ["pdf"], periode: "campagne", sansIdentite: true },
  audit: { libelle: "Audit de certification", detail: "HVE, AB, GlobalG.A.P. : XLSX + PDF, 12 derniers mois",
    rubriques: ["exploitation", "localisation", "ilots", "sol", "registre", "fertilisation", "irrigation", "documents"],
    formats: ["xlsx", "pdf"], periode: "12mois", sansIdentite: false },
  archives: { libelle: "Archives / autre logiciel", detail: "Tout, XLSX + GeoJSON + JSON, tout l'historique",
    rubriques: ["exploitation", "localisation", "ilots", "sol", "registre", "fertilisation", "biostimulants", "irrigation",
      "chantiers", "sante", "historique", "documents"], formats: ["xlsx", "geojson", "json"], periode: "tout", sansIdentite: false },
};

const RUBRIQUES = [
  ["exploitation", "Exploitation"], ["localisation", "Localisation et cadastre"], ["ilots", "Îlots et cultures"],
  ["sol", "Sol et analyses"], ["registre", "Registre phyto"], ["fertilisation", "Fertilisation"],
  ["biostimulants", "Biostimulants"], ["irrigation", "Irrigation"], ["chantiers", "Chantiers"],
  ["sante", "Santé des cultures"], ["historique", "Historique des campagnes"], ["documents", "Documents"],
];
const FORMATS = [["pdf", "PDF", "à lire, imprimer"], ["xlsx", "XLSX", "tableur, un onglet par rubrique"],
  ["csv", "CSV", "un fichier par onglet (zip)"], ["geojson", "GeoJSON", "contours pour un SIG"],
  ["json", "JSON", "pour un autre logiciel"]];
const PERIODES = [["campagne", "Campagne en cours"], ["annee", "Cette année"], ["12mois", "12 derniers mois"],
  ["tout", "Tout l'historique"], ["dates", "Dates"]];

const iso = (d) => d.toISOString().slice(0, 10);

function nomPeriode(periode, fiche, start, end) {
  const today = new Date();
  const annee = `${today.getFullYear()}-01-01_${today.getFullYear()}-12-31`;
  if (periode === "tout") return "tout";
  if (periode === "annee") return annee;
  if (periode === "12mois") return `${iso(new Date(today.getTime() - 365 * 864e5))}_${iso(today)}`;
  if (periode === "dates") return start && end ? `${start}_${end}` : "…";
  return fiche?.periode?.type === "campagne" ? `${fiche.periode.debut}_${fiche.periode.fin}` : annee;
}

export function pdfUrl(parcelId, query = "", sansIdentite = false) {
  const q = new URLSearchParams(query);
  if (sansIdentite) q.set("sans_identite", "1");
  const s = q.toString();
  return `${API}/export/parcel/${encodeURIComponent(parcelId)}/pdf${s ? `?${s}` : ""}`;
}

function Section({ numero, titre, children }) {
  return (
    <fieldset className="border-t pt-3 mt-3 first:border-t-0 first:pt-0 first:mt-0" style={{ borderColor: COL.line }}>
      <legend className="text-sm font-bold mb-2" style={{ color: COL.text }}>
        <span className="text-gray-400 mr-1.5">{numero}</span>{titre}
      </legend>
      {children}
    </fieldset>
  );
}

function Choix({ type = "radio", checked, onChange, label, detail, testId, disabled }) {
  return (
    <label className={`flex items-start gap-3 min-h-[44px] py-1.5 ${disabled ? "opacity-50" : "cursor-pointer"}`} data-testid={testId}>
      <input type={type} checked={checked} onChange={onChange} disabled={disabled}
        className="mt-1 w-4 h-4 shrink-0 accent-[#2D6A4F]" />
      <span className="min-w-0">
        <span className="block text-sm" style={{ color: COL.text }}>{label}</span>
        {detail && <span className="block text-xs text-gray-500">{detail}</span>}
      </span>
    </label>
  );
}

export default function ExtractionPanel({ parcelId, fiche, onClose, titre = true, className = "" }) {
  const ilots = useMemo(() => (Array.isArray(fiche?.ilots) ? fiche.ilots.filter((i) => i.ilot_id) : []), [fiche]);
  const brouillons = fiche && !isErreur(fiche.conformite) ? fiche.conformite.statuts.brouillon : 0;
  const nbDocuments = Array.isArray(fiche?.documents) ? fiche.documents.length : 0;

  const [usage, setUsage] = useState("controle");
  const [perimetre, setPerimetre] = useState("parcelle");
  const [ilotId, setIlotId] = useState("");
  const [periode, setPeriode] = useState(USAGES.controle.periode);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [rubriques, setRubriques] = useState(USAGES.controle.rubriques);
  const [formats, setFormats] = useState(USAGES.controle.formats);
  const [sansIdentite, setSansIdentite] = useState(false);
  const [busy, setBusy] = useState(false);
  const [enFile, setEnFile] = useState(0);

  useEffect(() => {
    const maj = async () => setEnFile((await demandesEnFile(parcelId)).length);
    maj();
    window.addEventListener(QUEUE_EVENT, maj);
    return () => window.removeEventListener(QUEUE_EVENT, maj);
  }, [parcelId]);

  const choisirUsage = (u) => {
    setUsage(u);
    if (USAGES[u]) {
      setRubriques(USAGES[u].rubriques);
      setFormats(USAGES[u].formats);
      setPeriode(USAGES[u].periode);
      setSansIdentite(USAGES[u].sansIdentite);
    }
  };
  // Toute modification d'un choix prédéfini passe en « Sur mesure »
  const surMesure = (fn) => (...args) => { fn(...args); setUsage("sur_mesure"); };
  const basculer = (liste, setListe, v) => setListe(liste.includes(v) ? liste.filter((x) => x !== v) : [...liste, v]);

  const n = perimetre === "ilot" && ilotId ? ilots.findIndex((i) => i.ilot_id === ilotId) + 1 : 0;
  const base = `veyerciel_${parcelId}${n ? `_ilot${n}` : ""}_${nomPeriode(periode, fiche, start, end)}`;
  const fichiers = [
    ...FORMATS.filter(([f]) => formats.includes(f)).map(([f]) => (f === "csv" ? `${base}_csv.zip` : `${base}.${f}`)),
    ...(rubriques.includes("documents") && nbDocuments ? [`${base}_documents.zip`] : []),
  ];
  const incomplet = !formats.length || (perimetre === "ilot" && !ilotId) || (periode === "dates" && !(start && end && start <= end));

  const extraire = async () => {
    const body = { usage, perimetre, ilot_id: perimetre === "ilot" ? ilotId : null, periode,
      start: periode === "dates" ? start : null, end: periode === "dates" ? end : null, rubriques, formats, sans_identite: sansIdentite };
    const enAttente = async () => {
      await mettreEnFile(parcelId, body);
      toast.info("Pas de réseau : la demande partira au retour du réseau. Le fichier sera ajouté aux Documents de la parcelle.");
    };
    if (!navigator.onLine) return enAttente();
    setBusy(true);
    try {
      const res = await authFetch(`${API}/parcels/${encodeURIComponent(parcelId)}/extraction`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(typeof err.detail === "string" ? err.detail : "Extraction impossible.");
        return;
      }
      const nom = (res.headers.get("Content-Disposition") || "").match(/filename="([^"]+)"/)?.[1] || fichiers[0];
      const blob = await res.blob();
      const u = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u; a.download = nom;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(u);
      toast.success("Fichier téléchargé");
    } catch {
      await enAttente();
    } finally {
      setBusy(false);
    }
  };

  return (
    <aside className={`bg-white rounded-2xl border p-4 ${className}`} style={{ borderColor: COL.line }} data-testid="fiche-extraction">
      {titre && (
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-base font-bold" style={{ color: COL.text, fontFamily: "Manrope, sans-serif" }}>Extraire les données</h2>
          {onClose && (
            <button type="button" onClick={onClose} className="w-11 h-11 -mr-2 flex items-center justify-center rounded-full hover:bg-black/5"
              aria-label="Fermer" data-testid="fiche-extraction-fermer">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      <Section numero="1" titre="Pour qui ?">
        {Object.entries(USAGES).map(([k, u]) => (
          <Choix key={k} checked={usage === k} onChange={() => choisirUsage(k)} label={u.libelle} detail={u.detail}
            testId={`extraction-usage-${k}`} />
        ))}
        <Choix checked={usage === "sur_mesure"} onChange={() => setUsage("sur_mesure")} label="Sur mesure"
          detail="Vos propres choix ci-dessous" testId="extraction-usage-sur_mesure" />
      </Section>

      <Section numero="2" titre="Périmètre">
        <Choix checked={perimetre === "parcelle"} onChange={surMesure(() => setPerimetre("parcelle"))} label="Cette parcelle"
          testId="extraction-perimetre-parcelle" />
        <Choix checked={perimetre === "ilot"} onChange={surMesure(() => setPerimetre("ilot"))} label="Un îlot"
          disabled={!ilots.length} detail={ilots.length ? null : "Aucun îlot tracé"} testId="extraction-perimetre-ilot" />
        {perimetre === "ilot" && (
          <select value={ilotId} onChange={surMesure((e) => setIlotId(e.target.value))} data-testid="extraction-ilot"
            className="w-full h-11 rounded-lg border px-3 text-sm bg-white" style={{ borderColor: COL.line }}>
            <option value="">Choisir l'îlot…</option>
            {ilots.map((i) => <option key={i.ilot_id} value={i.ilot_id}>{i.nom}</option>)}
          </select>
        )}
      </Section>

      <Section numero="3" titre="Période">
        <div className="flex flex-wrap gap-1.5">
          {PERIODES.map(([k, l]) => (
            <button key={k} type="button" onClick={surMesure(() => setPeriode(k))} data-testid={`extraction-periode-${k}`}
              className="min-h-[44px] px-3.5 rounded-full border text-sm"
              style={periode === k ? { background: COL.green, color: "#fff", borderColor: COL.green } : { borderColor: COL.line }}>
              {l}
            </button>
          ))}
        </div>
        {periode === "dates" && (
          <div className="grid grid-cols-2 gap-2 mt-2">
            <label className="text-xs text-gray-500">Du
              <input type="date" value={start} onChange={surMesure((e) => setStart(e.target.value))} data-testid="extraction-debut"
                className="w-full h-11 rounded-lg border px-2 text-sm text-black" style={{ borderColor: COL.line }} />
            </label>
            <label className="text-xs text-gray-500">Au
              <input type="date" value={end} onChange={surMesure((e) => setEnd(e.target.value))} data-testid="extraction-fin"
                className="w-full h-11 rounded-lg border px-2 text-sm text-black" style={{ borderColor: COL.line }} />
            </label>
          </div>
        )}
      </Section>

      <Section numero="4" titre="Contenu">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3">
          {RUBRIQUES.map(([k, l]) => (
            <Choix key={k} type="checkbox" checked={rubriques.includes(k)} label={l} testId={`extraction-rubrique-${k}`}
              onChange={surMesure(() => basculer(rubriques, setRubriques, k))} />
          ))}
        </div>
      </Section>

      <Section numero="5" titre="Formats">
        {FORMATS.map(([k, l, d]) => (
          <Choix key={k} type="checkbox" checked={formats.includes(k)} label={l} detail={d} testId={`extraction-format-${k}`}
            onChange={surMesure(() => basculer(formats, setFormats, k))} />
        ))}
        <Choix type="checkbox" checked={sansIdentite} onChange={surMesure(() => setSansIdentite(!sansIdentite))}
          label="Masquer SIRET, PACAGE et raison sociale" testId="extraction-sans-identite" />
      </Section>

      <div className="rounded-xl p-3 mt-4 space-y-2" style={{ background: "#F4F2EE" }} data-testid="extraction-recap">
        <div className="text-sm font-bold" style={{ color: COL.text }}>Vous allez recevoir</div>
        {fichiers.length > 1 && (
          <div className="text-xs text-gray-600">
            <FileArchive className="w-3.5 h-3.5 inline -mt-0.5 mr-1" />Un seul fichier zip, <span className="font-mono break-all">{base}.zip</span>, qui contient :
          </div>
        )}
        <ul className="space-y-0.5">
          {fichiers.map((f) => <li key={f} className="font-mono text-[11px] break-all" style={{ color: COL.text }}>{f}</li>)}
          {!fichiers.length && <li className="text-xs" style={{ color: COL.amber }}>Choisissez au moins un format.</li>}
        </ul>
        <div className="text-xs font-semibold" style={{ color: sansIdentite ? COL.green : COL.muted }}>
          {sansIdentite ? "Sans SIRET ni PACAGE" : "Contient SIRET et PACAGE"}
        </div>
        {rubriques.includes("registre") && brouillons > 0 && (
          <div className="text-xs flex gap-1.5 items-start" style={{ color: COL.amber }} data-testid="extraction-brouillons">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            {brouillons} brouillon(s) dans le registre : ils figurent marqués « brouillon » dans le journal, jamais dans le registre exporté.
          </div>
        )}
        {rubriques.includes("registre") && formats.some((f) => f === "xlsx" || f === "csv") && (
          <div className="text-xs text-gray-500">Les entrées validées incluses seront marquées « exportées » dans le registre.</div>
        )}
      </div>

      {enFile > 0 && (
        <p className="text-xs mt-3 flex items-center gap-1.5" style={{ color: COL.amber }} data-testid="extraction-en-file">
          <CloudOff className="w-3.5 h-3.5" /> {enFile} demande(s) en attente du réseau.
        </p>
      )}
      <button type="button" onClick={extraire} disabled={busy || incomplet} data-testid="extraction-valider"
        className="w-full h-12 mt-3 rounded-full text-sm font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-50"
        style={{ background: COL.green }}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Extraire
      </button>
    </aside>
  );
}
